import {
  BlockSuiteError,
  ErrorCode,
} from '@blocksuite/affine/global/exceptions';
import { NoopLogger } from '@blocksuite/affine/global/utils';
import {
  type Doc,
  type DocMeta,
  type IdGenerator,
  nanoid,
  type Workspace,
  type WorkspaceMeta,
} from '@blocksuite/affine/store';
import {
  BlobEngine,
  type BlobSource,
  MemoryBlobSource,
} from '@blocksuite/affine/sync';
import { Subject } from 'rxjs';
import type { Awareness } from 'y-protocols/awareness.js';
import type { Doc as YDoc } from 'yjs';

import type { FeatureFlagService } from '../../feature-flag';
import { DocImpl } from './doc';
import { WorkspaceMetaImpl } from './meta';

type WorkspaceOptions = {
  id?: string;
  rootDoc: YDoc;
  blobSource?: BlobSource;
  onLoadDoc?: (doc: YDoc) => void;
  onLoadAwareness?: (awareness: Awareness) => (() => void) | void;
  shouldLoadAwareness?: (docId: string) => boolean;
  onCreateDoc?: (docId?: string) => string;
  featureFlagService?: FeatureFlagService;
};

export class WorkspaceImpl implements Workspace {
  readonly blobSync: BlobEngine;

  readonly blockCollections = new Map<string, Doc>();

  private readonly registeredDocMetas = new Map<string, DocMeta>();

  readonly doc: YDoc;

  readonly id: string;

  readonly idGenerator: IdGenerator;

  meta: WorkspaceMeta;

  slots = {
    /* eslint-disable rxjs/finnish */
    docListUpdated: new Subject<void>(),
    /* eslint-enable rxjs/finnish */
  };

  get docs() {
    return this.blockCollections;
  }

  readonly onLoadDoc?: (doc: YDoc) => void;
  readonly onLoadAwareness?: (awareness: Awareness) => (() => void) | void;
  readonly shouldLoadAwareness?: (docId: string) => boolean;
  readonly onCreateDoc?: (docId?: string) => string;
  readonly featureFlagService?: FeatureFlagService;

  constructor({
    id,
    rootDoc,
    blobSource,
    onLoadDoc,
    onLoadAwareness,
    shouldLoadAwareness,
    onCreateDoc,
    featureFlagService,
  }: WorkspaceOptions) {
    this.id = id || '';
    this.featureFlagService = featureFlagService;
    this.doc = rootDoc;
    this.onLoadDoc = onLoadDoc;
    this.onLoadDoc?.(this.doc);
    this.onLoadAwareness = onLoadAwareness;
    this.shouldLoadAwareness = shouldLoadAwareness;
    this.onCreateDoc = onCreateDoc;

    blobSource = blobSource ?? new MemoryBlobSource();
    const logger = new NoopLogger();

    this.blobSync = new BlobEngine(blobSource, [], logger);

    this.idGenerator = nanoid;

    this.meta = new WorkspaceMetaImpl(this.doc);
    this._bindDocMetaEvents();
  }

  private _bindDocMetaEvents() {
    this.meta.docMetaAdded.subscribe(docId => {
      // Permission projections/database preload may register and connect this
      // document before root metadata arrives. Metadata adds identity, not a
      // second Y.Doc: replacing the live instance loses its local state and
      // collides with the sync engine on the next durable write.
      if (this.blockCollections.has(docId)) return;
      const doc = new DocImpl({
        id: docId,
        collection: this,
        doc: this.doc,
      });
      this.blockCollections.set(doc.id, doc);
    });

    this.meta.docMetaUpdated.subscribe(() => this.slots.docListUpdated.next());

    // SAFETY: Do NOT call doc.remove() on docMetaRemoved.
    //
    // doc.remove() calls rootDoc.getMap('spaces').delete(docId) which permanently
    // removes the sub-doc from the Y.js CRDT root document. This deletion
    // propagates to the backend and all other clients via Y.js sync.
    //
    // docMetaRemoved fires during Y.js CRDT merges when meta.pages is temporarily
    // empty or incomplete (e.g. during reconnect after backend restart). A single
    // false-positive event permanently destroys all doc references in the root doc.
    //
    // In-memory cleanup of blockCollections is safe, but doc.remove() must only
    // happen through explicit user-initiated permanent delete operations.
    this.meta.docMetaRemoved.subscribe(id => {
      console.warn(
        `[DocCollection] docMetaRemoved for ${id} — blockCollection cleared, doc.remove() SKIPPED (safety guard)`
      );
      this.blockCollections.delete(id);
      // doc.remove() intentionally NOT called — see safety comment above
    });
  }

  private _hasDoc(docId: string) {
    return this.docs.has(docId);
  }

  /**
   * sl-j143.4.2: Register a doc in blockCollections without writing to meta.pages.
   * Used for Members who get their accessible doc list from the server (GraphQL)
   * instead of from the root Y.js doc (which is FORBIDDEN for DSGVO reasons).
   *
   * Creates a DocImpl and adds it to blockCollections if not already present.
   * Does NOT write to meta.pages — the doc is registered locally only.
   */
  registerDoc(docId: string, meta: Partial<DocMeta> = {}): void {
    const previous = this.registeredDocMetas.get(docId);
    this.registeredDocMetas.set(docId, {
      id: docId,
      title: meta.title ?? previous?.title ?? '',
      tags: meta.tags ?? previous?.tags ?? [],
      createDate: meta.createDate ?? previous?.createDate ?? Date.now(),
      updatedDate: meta.updatedDate ?? previous?.updatedDate,
      favorite: meta.favorite ?? previous?.favorite,
      trash: meta.trash ?? previous?.trash,
    });
    if (this._hasDoc(docId)) {
      this.slots.docListUpdated.next();
      return;
    }
    const doc = new DocImpl({
      id: docId,
      collection: this,
      doc: this.doc,
    });
    this.blockCollections.set(doc.id, doc);
    this.slots.docListUpdated.next();
  }

  /**
   * sl-j143.4.2: Unregister a doc from blockCollections (access revoked).
   * Called when AccessibleDocCacheStore detects a doc was removed from the
   * user's accessible list (permission revoked by Owner/Admin).
   */
  unregisterDoc(docId: string): void {
    const doc = this._getDoc(docId);
    if (!doc) return;
    this.registeredDocMetas.delete(docId);
    this.blockCollections.delete(docId);
    this.slots.docListUpdated.next();
    doc.dispose();
  }

  getDocMeta(docId: string): DocMeta | undefined {
    return this.meta.getDocMeta(docId) ?? this.registeredDocMetas.get(docId);
  }

  /**
   * By default, only an empty doc will be created.
   * If the `init` parameter is passed, a `surface`, `note`, and `paragraph` block
   * will be created in the doc simultaneously.
   */
  createDoc(docId?: string): Doc {
    if (this.onCreateDoc) {
      const id = this.onCreateDoc(docId);
      const doc = this.getDoc(id);
      if (!doc) {
        throw new BlockSuiteError(
          ErrorCode.DocCollectionError,
          'create doc failed'
        );
      }
      return doc;
    }
    const id = docId ?? this.idGenerator();
    if (this._hasDoc(id)) {
      throw new BlockSuiteError(
        ErrorCode.DocCollectionError,
        'doc already exists'
      );
    }

    this.meta.addDocMeta({
      id,
      title: '',
      createDate: Date.now(),
      tags: [],
    });
    return this.getDoc(id) as Doc;
  }

  private _getDoc(docId: string): Doc | null {
    const space = this.docs.get(docId) as Doc | undefined;
    return space ?? null;
  }

  getDoc(docId: string): Doc | null {
    const doc = this._getDoc(docId);
    return doc;
  }

  removeDoc(docId: string) {
    const blockCollection = this._getDoc(docId);
    const docMeta = this.meta.getDocMeta(docId);
    if (docMeta) {
      this.meta.removeDocMeta(docId);
    } else {
      // sl-o0ra: Defensive — Cloud workspace docs may exist in PostgreSQL
      // (AccessibleDocCacheStore) but not in Y.js meta. No throw.
      console.warn(
        `[DocCollection] removeDoc: meta not found for ${docId}, skipping meta removal`
      );
    }

    if (blockCollection) {
      if (blockCollection instanceof DocImpl) {
        blockCollection.dispose({ clearBlocks: false });
      } else {
        blockCollection.dispose();
      }
    }
    this.blockCollections.delete(docId);
    this.registeredDocMetas.delete(docId);
  }

  dispose() {
    this.blockCollections.forEach(doc => doc.dispose());
    this.registeredDocMetas.clear();
  }
}
